/**
 * How a tenant reaches each port of a service, from what the host reported.
 *
 * Two ways in exist, and they are told apart by the manifest, not by guessing:
 *
 * - A global port exposed `as: 80` over TCP becomes an Ingress on the host
 *   (`ServiceExpose.IsIngress`), named by a hostname the lease status reports
 *   in `uris`. That name only answers once the host runs an ingress
 *   controller with HTTPS, which a provider says on chain with the attribute
 *   `capabilities/ingress/tls: "true"` (ops `provider_ingress`). Without it
 *   nothing listens on the host's port 80, so neither the name nor a
 *   Host-header request reaches the service: the address is shown, never
 *   offered as a link or a command (INSTANCE-ACCESS-AND-LEASE-VIEW-PLAN D1, D3).
 * - Every other global port becomes a Kubernetes NodePort. Its public port is
 *   whatever the cluster chose, reported in `forwarded_ports` as
 *   `externalPort` against the container `port` and protocol. The SDL's `as:`
 *   value is never that port, so it is never shown in its place.
 */

/** The parts of a reported service this reads (structurally, `ServiceView`). */
export interface AccessService { name: string; uris: readonly string[] }

/** The parts of a reported forwarded port this reads (structurally, `EndpointView`). */
export interface AccessEndpoint { service: string; address: string; host: string; port: number | null; externalPort: number | null; proto: string }

/** The provider attribute that says its ingress serves tenant hostnames over HTTPS. */
export const INGRESS_TLS_ATTRIBUTE = 'capabilities/ingress/tls'

/**
 * What the console knows about a host's ingress. Only `not-public` says the
 * host lacks HTTPS: that needs its attributes read and the flag absent. Until
 * then (`checking`), or when the read failed (`unreadable`), the console says
 * it does not know rather than stating the opposite of what may be true.
 */
export type IngressState = 'https' | 'not-public' | 'checking' | 'unreadable'

/** The ingress state from the provider read: its attributes, and whether it is loading or failed. */
export function ingressState(attributes: Record<string, string> | null | undefined, { loading, failed }: { loading: boolean; failed: boolean }): IngressState {
  if (ingressServesHttps(attributes)) return 'https'
  if (attributes) return 'not-public'
  return failed ? 'unreadable' : loading ? 'checking' : 'unreadable'
}

/** Whether a provider's on-chain attributes say its ingress serves HTTPS. */
export function ingressServesHttps(attributes: Record<string, string> | null | undefined): boolean {
  return attributes?.[INGRESS_TLS_ATTRIBUTE] === 'true'
}

/** One port the tenant sent, as the manifest builds it: container port, external port, protocol. */
export interface SentPort {
  port: number
  externalPort: number
  proto: string
}

export type PortAccess =
  /** An ingress hostname the host serves over HTTPS: a working link. */
  | { kind: 'https'; url: string; hostname: string }
  /** An ingress hostname the host does not serve publicly yet: shown, never linked. */
  | { kind: 'not-public'; hostname: string }
  /** A NodePort: the host's address and the port the cluster chose. */
  | { kind: 'nodeport'; ip: string; publicPort: number; port: number; protocol: string; address: string }
  /** A port the tenant asked for that the host has not reported yet. */
  | { kind: 'pending'; port: number; protocol: string; ingress: boolean }

/** A manifest expose that the host serves through its ingress (`IsIngress`). */
export function isIngressPort(port: SentPort): boolean {
  return port.externalPort === 80 && port.proto.toUpperCase() === 'TCP'
}

/** A reported URI's bare hostname, or null when it is not one. */
function hostnameOf(uri: string): string | null {
  const trimmed = uri.trim()
  // Only http(s) may carry a scheme; anything else (`javascript:`…) is not a hostname.
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed) && !/^https?:\/\//i.test(trimmed) && !/^[^:]+:\d+(?:[/?#]|$)/.test(trimmed)) return null
  const value = trimmed.replace(/^https?:\/\//i, '').replace(/[/?#].*$/, '')
  return /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*(?::\d{1,5})?$/i.test(value) ? value : null
}

/**
 * Every way into `service`, in a stable order: ingress hostnames first, then
 * NodePorts in the order the host reported them, then ports the tenant sent
 * that the host has not reported (only when `sent` is known).
 */
export function serviceAccess(service: AccessService, endpoints: readonly AccessEndpoint[], { https, sent = null }: { https: boolean; sent?: readonly SentPort[] | null }): PortAccess[] {
  const out: PortAccess[] = []
  for (const uri of service.uris) {
    const hostname = hostnameOf(uri)
    if (!hostname) continue
    out.push(https ? { kind: 'https', url: `https://${hostname}`, hostname } : { kind: 'not-public', hostname })
  }
  const forwarded = endpoints.filter((endpoint) => endpoint.service === service.name && endpoint.externalPort !== null)
  for (const endpoint of forwarded) {
    out.push({ kind: 'nodeport', ip: endpoint.host, publicPort: endpoint.externalPort!, port: endpoint.port ?? 0, protocol: endpoint.proto, address: endpoint.address })
  }
  for (const port of sent ?? []) {
    const ingress = isIngressPort(port)
    const reported = ingress
      ? out.some((access) => access.kind === 'https' || access.kind === 'not-public')
      : forwarded.some((endpoint) => endpoint.port === port.port && endpoint.proto === port.proto.toLowerCase())
    if (!reported) out.push({ kind: 'pending', port: port.port, protocol: port.proto.toLowerCase(), ingress })
  }
  return out
}

/** The address a tenant copies for one way in, or null while it is pending. */
export function accessAddress(access: PortAccess): string | null {
  switch (access.kind) {
    case 'https': return access.url
    case 'not-public': return access.hostname
    case 'nodeport': return access.address
    default: return null
  }
}

/** Where an Open button may go: an HTTPS ingress, or a TCP NodePort over http. Null when nothing can work. */
export function accessHref(access: PortAccess): string | null {
  if (access.kind === 'https') return access.url
  // The host names itself; anything but a bare host and port is not a link.
  if (access.kind === 'nodeport' && access.protocol === 'tcp' && /^[a-z0-9.-]+$/i.test(access.ip) && hostnameOf(access.ip) === access.ip) return `http://${access.ip}:${access.publicPort}`
  return null
}
